/**
 * Commerce7 (and similar) call window.scrollTo(0, 0) on init, which with
 * CSS scroll-behavior:smooth animates away from deep-linked hash targets.
 * Allow GSAP ScrollTrigger's sync refresh (scroll to 0, then restore) to
 * finish, then re-apply the hash if scroll was left at the top.
 */
export function preserveHashScroll() {
  if (typeof window === 'undefined' || !window.location.hash) return

  const hash = window.location.hash
  const origScrollTo = window.scrollTo.bind(window)

  const topFromArgs = (args: unknown[]): number | undefined => {
    if (typeof args[0] === 'number') return args[1] as number
    if (args[0] && typeof args[0] === 'object') {
      return (args[0] as ScrollToOptions).top
    }
    return undefined
  }

  const scrollToHashInstant = () => {
    const el = document.querySelector(hash)
    if (!el) return
    const html = document.documentElement
    const prev = html.style.scrollBehavior
    html.style.scrollBehavior = 'auto'
    const y = el.getBoundingClientRect().top + window.scrollY
    origScrollTo(0, y)
    html.style.scrollBehavior = prev
  }

  window.scrollTo = (...args: Parameters<typeof window.scrollTo>) => {
    const top = topFromArgs(args as unknown[])
    const shouldGuard = top === 0 && window.scrollY > 50 && window.location.hash === hash

    if (shouldGuard) {
      const html = document.documentElement
      const prev = html.style.scrollBehavior
      html.style.scrollBehavior = 'auto'

      if (typeof args[0] === 'number') {
        origScrollTo(args[0], args[1] as number)
      } else {
        origScrollTo({ ...(args[0] as ScrollToOptions), behavior: 'auto' })
      }

      // GSAP restores scroll synchronously in the same turn; Commerce7 does not.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (window.location.hash === hash && window.scrollY < 50) {
            scrollToHashInstant()
          }
          html.style.scrollBehavior = prev
        })
      })
      return
    }

    return origScrollTo(...args)
  }
}

preserveHashScroll()
